import {
	createNamedContext,
	useRequiredContext,
} from "@repo/utils/hooks/useRequiredContext";
import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	type PropsWithChildren,
} from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { IconName } from "@repo/ui";

export enum Experiment {
	DeveloperMode = "developer-mode",
	RefreshedDesign = "refreshed-design",
}

export type ExperimentConfig = {
	name: string;
	description: string;
	defaultValue: boolean;
	icon: IconName;
};

export const EXPERIMENTS: Record<Experiment, ExperimentConfig> = {
	[Experiment.DeveloperMode]: {
		name: "Developer Mode",
		description: "Show developer tools in the left pane",
		defaultValue: false,
		icon: "Construction",
	},
	[Experiment.RefreshedDesign]: {
		name: "Refreshed Design",
		description:
			"Enable redesigned activity logs with improved visual hierarchy",
		defaultValue: false,
		icon: "Sparkles",
	},
};

type ExperimentsState = {
	values: Partial<Record<Experiment, boolean>>;
	setValue: (id: Experiment, value: boolean) => void;
};

const STORAGE_KEY = "web:experiments:v1";

function createExperimentsStore() {
	return create<ExperimentsState>()(
		persist(
			(set) => ({
				values: {},
				setValue: (id, value) =>
					set((state) => ({ values: { ...state.values, [id]: value } })),
			}),
			{
				name: STORAGE_KEY,
				version: 1,
				partialize: (state) => ({ values: state.values }),
				skipHydration: typeof window === "undefined",
			},
		),
	);
}

type ExperimentsStore = ReturnType<typeof createExperimentsStore>;

const ExperimentsContext = createNamedContext<ExperimentsStore | undefined>(
	"ExperimentsContext",
	undefined,
);

export function ExperimentsProvider({ children }: PropsWithChildren) {
	const store = useMemo(() => createExperimentsStore(), []);
	return (
		<ExperimentsContext.Provider value={store}>
			{children}
		</ExperimentsContext.Provider>
	);
}

function useExperimentsStore(): ExperimentsStore {
	return useRequiredContext(ExperimentsContext);
}

function useHasHydrated(store: ExperimentsStore): boolean {
	const [hydrated, setHydrated] = useState(store.persist.hasHydrated());

	useEffect(() => {
		const unsub = store.persist.onFinishHydration(() => setHydrated(true));
		return unsub;
	}, [store]);

	return hydrated;
}

export function useExperiment(
	experiment: Experiment,
): [boolean, (value: boolean) => void] {
	const store = useExperimentsStore();
	const hasHydrated = useHasHydrated(store);

	const current = store((s) => s.values[experiment]);
	const setValue = store((s) => s.setValue);

	const config = EXPERIMENTS[experiment];
	const defaultValue = config?.defaultValue ?? false;
	const value = (hasHydrated ? current : undefined) ?? defaultValue;

	const updater = useCallback(
		(next: boolean) => {
			if (!hasHydrated) {
				return;
			}
			setValue(experiment, next);
		},
		[experiment, hasHydrated, setValue],
	);

	return [value, updater];
}
